

import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import image0 from "./image/image0.png"; // green
import image1 from "./image/image1.png"; // gray (fallback / offline)
import image2 from "./image/image2.png"; // yellow
import image3 from "./image/image3.png"; // red

// --- Function to pick marker image based on timestamp ---
const getMarkerImage = (timestamp) => {
  if (!timestamp) return image1; // fallback to gray
  const ts = new Date(timestamp).getTime();
  const now = Date.now();
  const diffInMinutes = (now - ts) / (1000 * 60);

  if (diffInMinutes <= 5) return image0; // ✅ green if within 5 minutes
  if (diffInMinutes <= 60 * 24) return image2; // yellow if within 24 hours
  if (diffInMinutes <= 60 * 48) return image3; // red if within 48 hours
  return image1; // gray otherwise
};

// const getMarkerImage = (timestamp) => {
//   if (!timestamp) return image1; // fallback to gray
//   const ts = new Date(timestamp).getTime();
//   const now = Date.now();
//   const diffInHours = (now - ts) / (1000 * 60 * 60);

//   if (diffInHours <= 5) return image0; // green
//   if (diffInHours <= 24) return image2; // yellow
//   if (diffInHours <= 48) return image3; // red
//   return image1; // gray
// };

// --- Setup Leaflet icon defaults ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// --- Create custom icon ---
const createCustomIcon = (imageUrl) => {
  return new L.Icon({
    iconUrl: imageUrl,
    iconSize: [64, 64],
    iconAnchor: [32, 64],
    popupAnchor: [0, -64],
  });
};

// --- Change View Helper (zooming on selected device) ---
const ChangeView = ({ bounds, selectedDevice }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedDevice) {
      const telemetry = selectedDevice.telemetry || {};
      const lat = telemetry.lat ?? selectedDevice.lat;
      const lng = telemetry.lng ?? telemetry.lon ?? selectedDevice.lng;
      if (typeof lat === "number" && typeof lng === "number") {
        map.setView([lat, lng], 15);
      }
    } else if (bounds && bounds.length) {
      map.fitBounds(bounds, { maxZoom: 6 });
    }
  }, [bounds, selectedDevice, map]);

  return null;
};

// --- Helper to show battery icon ---
const getBatteryIcon = (level) => {
  if (typeof level !== "number") return "❓";
  if (level >= 90) return "🔋";
  if (level >= 60) return "🔋";
  if (level >= 30) return "🔋";
  if (level > 0) return "🔋";
  return "❌";
};

// --- Main MapView component ---
const MapView = ({ selectedDevice, filteredDevices }) => {
  const defaultPosition = [28.39, 77.209];
  const defaultZoom = 6;

  const createMarker = (device, isSelected = false) => {
    const telemetry = device.telemetry || {};
    const lat = telemetry.lat ?? device.lat;
    const lng = telemetry.lng ?? telemetry.lon ?? device.lng;
    const name = telemetry.id ?? device.device?.name ?? device.name ?? device.id ?? "Unnamed Device";
    const battery = telemetry.battery_level ?? device.battery_level ?? null;
    const status = device.status;
    const rssi =telemetry.rssi ?? device.rssi
    const timestamp = telemetry.timestamp ?? telemetry.cloud_ts ?? device.cloud_ts ?? telemetry.ts??  null; // updated to prioritize cloud_ts

    if (typeof lat === "number" && typeof lng === "number") {
      return { lat, lng, name, battery, status, isSelected, timestamp,rssi};
    }
    return null;
  };

  const markers = useMemo(() => {
    if (selectedDevice) {
      const marker = createMarker(selectedDevice, true);
      return marker ? [marker] : [];
    }
    return filteredDevices.map((device) => createMarker(device)).filter(Boolean);
  }, [selectedDevice, filteredDevices]);

  const bounds = useMemo(() => markers.map((m) => [m.lat, m.lng]), [markers]);

  return (
    <MapContainer
      center={defaultPosition}
      zoom={defaultZoom}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
      />

      <ChangeView bounds={bounds} selectedDevice={selectedDevice} />

      {markers.map((marker, idx) => {
        const markerImage = getMarkerImage(marker.timestamp);
        const markerIcon = createCustomIcon(markerImage);

        return (
          <Marker
            key={`marker-${idx}`}
            position={[marker.lat, marker.lng]}
            icon={markerIcon}
          >
            <Popup>
              <strong>{marker.isSelected ? "⭐ " : ""}{marker.name}</strong>
              <br />
              Battery: {marker.battery ?? "N/A"}% {getBatteryIcon(marker.battery)}
              <br />
              Status: {marker.status || "Unknown"}
              <br />
              Rssi: {marker.rssi || "Unknown"}
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

export default MapView;
